import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { BottomNav } from '../components/BottomNav';
export function Analytics() {
  const navigate = useNavigate();
  const [showExportSuccess, setShowExportSuccess] = useState(false);
  const metrics = [{
    label: 'Leads this week',
    value: 12,
    emoji: '🔹',
    color: '#2196F3'
  }, {
    label: 'Active Clients',
    value: 18,
    emoji: '🟢',
    color: '#25D366'
  }, {
    label: 'Renewals Due',
    value: 3,
    emoji: '🟡',
    color: '#FFB300'
  }, {
    label: 'Follow-ups Pending',
    value: 5,
    emoji: '🔴',
    color: '#F44336'
  }];
  const clientStageData = [{
    name: 'Leads',
    value: 12,
    color: '#2196F3'
  }, {
    name: 'Active',
    value: 18,
    color: '#25D366'
  }, {
    name: 'Inactive',
    value: 24,
    color: '#9E9E9E'
  }];
  const conversionData = [{
    month: 'Sep',
    rate: 45
  }, {
    month: 'Oct',
    rate: 52
  }, {
    month: 'Nov',
    rate: 48
  }, {
    month: 'Dec',
    rate: 58
  }];
  const messageActivityData = [{
    day: 'Mon',
    messages: 24
  }, {
    day: 'Tue',
    messages: 31
  }, {
    day: 'Wed',
    messages: 28
  }, {
    day: 'Thu',
    messages: 35
  }, {
    day: 'Fri',
    messages: 29
  }, {
    day: 'Sat',
    messages: 18
  }, {
    day: 'Sun',
    messages: 15
  }];
  const handleExport = () => {
    setShowExportSuccess(true);
    setTimeout(() => setShowExportSuccess(false), 3000);
  };
  return <div className="min-h-screen bg-[#F5F5F5] pb-20">
      <div className="bg-white shadow-sm sticky top-0 z-20">
        <div className="p-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-gray-600 hover:text-gray-800">
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-semibold text-[#212121]">
            Dashboard Analytics
          </h1>
        </div>
      </div>
      <div className="p-4 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          {metrics.map((metric, index) => <div key={index} className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">{metric.emoji}</span>
                <h3 className="text-xs font-medium text-gray-600">
                  {metric.label}
                </h3>
              </div>
              <p className="text-3xl font-bold" style={{
            color: metric.color
          }}>
                {metric.value}
              </p>
            </div>)}
        </div>
        <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
          <h2 className="text-base font-semibold text-[#212121] mb-4">
            Client Stage Distribution
          </h2>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={clientStageData} cx="50%" cy="50%" labelLine={false} label={({
              name,
              percent
            }) => `${name} ${(percent * 100).toFixed(0)}%`} outerRadius={70} fill="#8884d8" dataKey="value">
                {clientStageData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
          <h2 className="text-base font-semibold text-[#212121] mb-4">
            Conversion Rate per Month
          </h2>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={conversionData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
              <XAxis dataKey="month" tick={{
              fontSize: 12
            }} stroke="#9E9E9E" />
              <YAxis tick={{
              fontSize: 12
            }} stroke="#9E9E9E" />
              <Tooltip contentStyle={{
              backgroundColor: '#FFF',
              border: '1px solid #E0E0E0',
              borderRadius: '8px'
            }} />
              <Bar dataKey="rate" fill="#25D366" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <p className="text-xs text-gray-500 text-center mt-2">
            Percentage of leads converted to active clients
          </p>
        </div>
        <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
          <h2 className="text-base font-semibold text-[#212121] mb-4">
            Message Activity (Last 7 Days)
          </h2>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={messageActivityData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
              <XAxis dataKey="day" tick={{
              fontSize: 12
            }} stroke="#9E9E9E" />
              <YAxis tick={{
              fontSize: 12
            }} stroke="#9E9E9E" />
              <Tooltip contentStyle={{
              backgroundColor: '#FFF',
              border: '1px solid #E0E0E0',
              borderRadius: '8px'
            }} />
              <Line type="monotone" dataKey="messages" stroke="#25D366" strokeWidth={3} dot={{
              fill: '#25D366',
              r: 4
            }} activeDot={{
              r: 6
            }} />
            </LineChart>
          </ResponsiveContainer>
          <p className="text-xs text-gray-500 text-center mt-2">
            Total messages sent per day
          </p>
        </div>
        <button onClick={handleExport} className="w-full bg-[#25D366] text-white font-semibold py-4 rounded-xl shadow-md hover:bg-[#20BD5A] transition-colors flex items-center justify-center gap-2">
          <DownloadIcon className="w-5 h-5" />
          Export CSV
        </button>
      </div>
      <BottomNav />
      {showExportSuccess && <div className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-[#212121] text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-2 animate-slide-up z-50">
          <span className="text-[#25D366]">✓</span>
          Analytics exported successfully
        </div>}
    </div>;
}